/**
 * CRA3D Paywall — admin product management + test-entitlement grants.
 *
 * Prices on this screen are ALWAYS labelled "DRAFT — not visible to
 * customers". This is the only surface where they appear, and the
 * customer-facing /plans page is verified server-side to strip them.
 *
 * Grants shown here are the admin test-grant path (source =
 * "admin_test_grant"). A future Stripe TEST-mode integration will add
 * `source = "stripe_test_purchase"` grants alongside these without
 * disturbing this UI.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Package, Sparkles, ShieldCheck, ShieldOff, UserCog, Trash2,
  ArrowLeft, RefreshCw,
} from "lucide-react";


const PROD_FIELDS_INITIAL = {
  slug: "",
  name: "",
  description: "",
  product_type: "addon",
  capabilities: "{}",
  draft_prices: "",
  active: true,
  display_order: 100,
};


function ProductEditDialog({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState(PROD_FIELDS_INITIAL);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setF({
        slug: initial.slug || "",
        name: initial.name || "",
        description: initial.description || "",
        product_type: initial.product_type || "addon",
        capabilities: JSON.stringify(initial.capabilities || {}, null, 2),
        draft_prices: initial.draft_prices ? JSON.stringify(initial.draft_prices) : "",
        active: !!initial.active,
        display_order: initial.display_order ?? 100,
      });
    } else {
      setF(PROD_FIELDS_INITIAL);
    }
    setErr(null);
  }, [open, initial]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      let caps;
      try { caps = JSON.parse(f.capabilities || "{}"); }
      catch (_) { throw new Error("`capabilities` must be valid JSON."); }
      let prices = null;
      if (f.draft_prices?.trim()) {
        try { prices = JSON.parse(f.draft_prices); }
        catch (_) { throw new Error("`draft_prices` must be valid JSON like {\"USD\":4900}."); }
      }
      const body = {
        slug: f.slug.trim(),
        name: f.name.trim(),
        description: f.description,
        product_type: f.product_type,
        capabilities: caps,
        draft_prices: prices,
        active: f.active,
        display_order: parseInt(f.display_order, 10) || 100,
      };
      await api.put("/admin/products", body);
      toast.success(initial ? "Product updated." : "Product created.");
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.message || extractApiError(e, "Save failed."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="admin-products-edit-dialog"
        className="bg-bg-surface border-border-subtle text-fg-primary max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {initial ? "Edit product" : "New product"}
          </DialogTitle>
          <DialogDescription className="text-fg-secondary">
            Capabilities are merged into the customer's entitlement when
            this product is granted. Draft prices are stored here but
            never shown to customers while payments are inactive.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="prod-slug">Slug (natural key)</Label>
            <Input
              id="prod-slug"
              data-testid="admin-products-edit-slug"
              value={f.slug}
              onChange={(e) => setF({ ...f, slug: e.target.value })}
              placeholder="e.g., full_experience"
              disabled={!!initial}
              className="bg-bg-base border-border-subtle text-fg-primary"
            />
          </div>
          <div>
            <Label htmlFor="prod-name">Name</Label>
            <Input
              id="prod-name"
              data-testid="admin-products-edit-name"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className="bg-bg-base border-border-subtle text-fg-primary"
            />
          </div>
          <div>
            <Label htmlFor="prod-desc">Description (customer-visible)</Label>
            <Textarea
              id="prod-desc"
              data-testid="admin-products-edit-description"
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              className="bg-bg-base border-border-subtle text-fg-primary min-h-20"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="prod-type">Type</Label>
              <select
                id="prod-type"
                data-testid="admin-products-edit-type"
                value={f.product_type}
                onChange={(e) => setF({ ...f, product_type: e.target.value })}
                className="mt-1 w-full rounded-md bg-bg-base border border-border-subtle text-fg-primary p-2"
              >
                <option value="plan">Plan</option>
                <option value="addon">Add-on</option>
              </select>
            </div>
            <div>
              <Label htmlFor="prod-order">Display order</Label>
              <Input
                id="prod-order"
                type="number"
                data-testid="admin-products-edit-order"
                value={f.display_order}
                onChange={(e) => setF({ ...f, display_order: e.target.value })}
                className="bg-bg-base border-border-subtle text-fg-primary"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="prod-caps">Capabilities (JSON)</Label>
            <Textarea
              id="prod-caps"
              data-testid="admin-products-edit-caps"
              value={f.capabilities}
              onChange={(e) => setF({ ...f, capabilities: e.target.value })}
              className="bg-bg-base border-border-subtle text-fg-primary min-h-32 font-mono text-xs"
            />
          </div>
          <div>
            <Label htmlFor="prod-prices">
              Draft prices (JSON, cents) — DRAFT, not visible to customers
            </Label>
            <Input
              id="prod-prices"
              data-testid="admin-products-edit-prices"
              value={f.draft_prices}
              onChange={(e) => setF({ ...f, draft_prices: e.target.value })}
              placeholder='e.g., {"USD": 4900, "CAD": 6900}'
              className="bg-bg-base border-border-subtle text-fg-primary font-mono text-xs"
            />
            <p className="mt-1 text-[11px] italic text-fg-muted">
              Stored on the row only. Customer-facing endpoints strip
              this field while `PAYMENTS_ACTIVE=false` (env-only gate).
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => setF({ ...f, active: e.target.checked })}
              data-testid="admin-products-edit-active"
            />
            Active (visible on customer plans page)
          </label>
        </div>
        {err && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}
        <DialogFooter className="gap-2 sm:justify-end mt-4">
          <Button variant="ghost" onClick={onClose} disabled={busy}
                   data-testid="admin-products-edit-cancel"
                   className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}
                   data-testid="admin-products-edit-save"
                   className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function GrantDialog({ open, onClose, product, onGranted }) {
  const [uid, setUid] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setUid(""); setNote(""); setErr(null); }
  }, [open]);

  const grant = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/admin/products/${product.id}/grant`, {
        user_id: uid.trim(), note: note.trim() || null,
      });
      toast.success(`Granted to ${r.data.target_email}.`);
      onGranted();
      onClose();
    } catch (e) {
      setErr(extractApiError(e, "Grant failed."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="admin-products-grant-dialog"
        className="bg-bg-surface border-border-subtle text-fg-primary"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            Grant "{product?.name}" — test access
          </DialogTitle>
          <DialogDescription className="text-fg-secondary">
            This is the admin test-grant path. No customer is charged.
            The grant is auditable and revocable at any time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="grant-uid">Target user id</Label>
            <Input
              id="grant-uid"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              placeholder="e.g., 7d02405a-1b41-4383-b2b4-b0719a6f3815"
              data-testid="admin-products-grant-uid"
              className="bg-bg-base border-border-subtle text-fg-primary font-mono text-xs"
            />
          </div>
          <div>
            <Label htmlFor="grant-note">Note (optional)</Label>
            <Textarea
              id="grant-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
              data-testid="admin-products-grant-note"
              className="bg-bg-base border-border-subtle text-fg-primary min-h-16"
            />
          </div>
        </div>
        {err && <Alert variant="destructive" className="mt-2"><AlertDescription>{err}</AlertDescription></Alert>}
        <DialogFooter className="gap-2 sm:justify-end mt-4">
          <Button variant="ghost" onClick={onClose} disabled={busy}
                   data-testid="admin-products-grant-cancel"
                   className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
            Cancel
          </Button>
          <Button onClick={grant} disabled={busy || !uid.trim()}
                   data-testid="admin-products-grant-confirm"
                   className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
            {busy ? "Granting…" : "Grant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [grants, setGrants] = useState([]);
  const [paymentsActive, setPaymentsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [granting, setGranting] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const [pl, gl] = await Promise.all([
        api.get("/admin/products?include_inactive=true"),
        api.get("/admin/products/grants?include_revoked=false"),
      ]);
      setProducts(pl.data.products || []);
      setPaymentsActive(!!pl.data.payments_active);
      setGrants(gl.data.grants || []);
    } catch (e) {
      setErr(extractApiError(e, "Couldn't load admin product catalog."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/activate`, { active: !p.active });
      toast.success(p.active ? `Deactivated ${p.name}.` : `Activated ${p.name}.`);
      load();
    } catch (e) { toast.error(extractApiError(e, "Toggle failed.")); }
  };

  const revoke = async (gid) => {
    try {
      await api.delete(`/admin/products/grants/${gid}`);
      toast.success("Grant revoked.");
      load();
    } catch (e) { toast.error(extractApiError(e, "Revoke failed.")); }
  };

  const productsById = useMemo(
    () => Object.fromEntries((products || []).map((p) => [p.id, p])),
    [products],
  );

  return (
    <AppShell>
      <div
        data-testid="admin-products-page"
        className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <Link to="/admin/m5" data-testid="admin-products-back"
                   className="text-xs text-fg-secondary hover:text-fg-primary flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Back to admin overview
            </Link>
            <h1 className="mt-2 text-2xl sm:text-3xl font-heading font-medium text-fg-primary flex items-center gap-2">
              <Package className="h-5 w-5" aria-hidden="true" />
              Product catalog + entitlement grants
            </h1>
            <p className="mt-2 text-sm text-fg-secondary max-w-2xl">
              Manage the plans and add-ons customers see on /plans.
              Prices below are DRAFT — they are never shown to customers
              while payments are inactive.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              data-testid="admin-products-payments-active-badge"
              variant={paymentsActive ? "default" : "outline"}
              className={paymentsActive
                ? "bg-accent-warm text-bg-base"
                : "border-border-subtle text-fg-secondary"}
            >
              {paymentsActive ? "PAYMENTS ACTIVE" : "Payments inactive"}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              data-testid="admin-products-refresh-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Refresh
            </Button>
            <Button
              onClick={() => { setEditing(null); setEditOpen(true); }}
              data-testid="admin-products-new-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              New product
            </Button>
          </div>
        </div>

        <Alert
          data-testid="admin-products-payments-gate-note"
          className="mb-6 border-border-subtle bg-bg-surface"
        >
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="text-fg-primary text-xs">
            <strong>Payments-active gate is env-var only.</strong> To
            enable live payments, set <code>PAYMENTS_ACTIVE=true</code> in
            <code> backend/.env</code> and restart the backend. There is
            no API that flips this — this UI reflects the running
            backend's environment.
          </AlertDescription>
        </Alert>

        {err && <Alert variant="destructive" className="mb-4" data-testid="admin-products-error"><AlertDescription>{err}</AlertDescription></Alert>}

        {loading ? (
          <p className="text-sm text-fg-secondary text-center py-8"
             data-testid="admin-products-loading">Loading catalog…</p>
        ) : (
          <>
            <h2 className="text-sm font-medium text-fg-primary mb-3">Products</h2>
            <ul className="space-y-3" data-testid="admin-products-list">
              {products.map((p) => (
                <li
                  key={p.id}
                  data-testid={`admin-products-row-${p.slug}`}
                  className="rounded-lg border border-border-subtle bg-bg-surface p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-wider text-fg-secondary">
                        {p.product_type} · order {p.display_order}
                        {!p.active && <span className="ml-2 text-fg-muted">INACTIVE</span>}
                      </p>
                      <p className="mt-1 text-base font-medium text-fg-primary">{p.name}</p>
                      <p className="mt-1 text-xs text-fg-secondary">{p.description}</p>
                      <p className="mt-1 text-[10px] text-fg-muted font-mono">
                        slug: {p.slug} · id: {p.id?.slice(0, 8)}
                      </p>
                      <div
                        data-testid={`admin-products-row-${p.slug}-draft-prices`}
                        className="mt-2 text-xs"
                      >
                        <span className="uppercase tracking-widest text-[10px] text-amber-500 mr-2">
                          DRAFT — not visible to customers
                        </span>
                        <span className="font-mono text-fg-primary">
                          {p.draft_prices ? JSON.stringify(p.draft_prices) : "(no prices set)"}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="ghost"
                               onClick={() => { setEditing(p); setEditOpen(true); }}
                               data-testid={`admin-products-row-${p.slug}-edit-btn`}
                               className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost"
                               onClick={() => toggleActive(p)}
                               data-testid={`admin-products-row-${p.slug}-toggle-btn`}
                               className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                        {p.active ? <ShieldOff className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                                   : <ShieldCheck className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                        {p.active ? "Deactivate" : "Activate"}
                      </Button>
                      {p.active && (
                        <Button size="sm"
                                 onClick={() => setGranting(p)}
                                 data-testid={`admin-products-row-${p.slug}-grant-btn`}
                                 className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                          <UserCog className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                          Grant to user
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <h2 className="mt-8 text-sm font-medium text-fg-primary mb-3">
              Active grants ({grants.length})
            </h2>
            {grants.length === 0 ? (
              <p className="text-xs text-fg-secondary"
                 data-testid="admin-products-grants-empty">
                No active grants right now.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="admin-products-grants-list">
                {grants.map((g) => (
                  <li
                    key={g.id}
                    data-testid={`admin-products-grant-row-${g.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-subtle bg-bg-surface p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-fg-secondary">
                        {productsById[g.product_id]?.name || g.product?.name || g.product_slug}
                      </p>
                      <p className="text-[11px] text-fg-muted font-mono">
                        to {g.target_email} · granted by {g.granted_by} · {new Date(g.granted_at).toLocaleString()}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost"
                             onClick={() => revoke(g.id)}
                             data-testid={`admin-products-grant-row-${g.id}-revoke-btn`}
                             className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                      <Trash2 className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      Revoke
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <ProductEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initial={editing}
        onSaved={load}
      />
      <GrantDialog
        open={!!granting}
        onClose={() => setGranting(null)}
        product={granting}
        onGranted={load}
      />
    </AppShell>
  );
}
